import type { FormEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { SearchMenuStates } from 'components/Search/type'
import { getRecentSearches, getHotelSearchSummary } from 'components/Search/utils'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { buildDisplayOnlySearchItem, getSubmitSearchItem } from '../components/ListingDrawerSearchUtils'
import { getHotelSearchDateState, getHotelDateSelectionFromSearchState, isListingDrawerHotelDateSelection, type ListingDrawerHotelDateSelection } from '../components/ListingDrawerSearchDates'
import type { HotelListingSearchSubmitParams } from '../components/HotelListingDrawer/types'
import type { ListingDrawerHotelDateGuestsHandle } from '../components/ListingDrawerHotelDateGuestsChips'

import { createHotelSearchState } from '../components/HotelListingDrawer/hotelSearchState'
import { HOTEL_DRAWER_MAX_GUESTS } from '../components/HotelListingDrawer/constants'

export interface HotelListingSearchControlsParams {
  searchValue: string
  searchItem?: App.SearchItem
  hotelDateSelection: ListingDrawerHotelDateSelection
  onHotelDateSelectionChange: (selection: ListingDrawerHotelDateSelection, target?: App.SearchItem) => void
  rooms: Array<App.Occupants>
  maxGuests?: number
  onRoomsChange: (rooms: Array<App.Occupants>) => void
  onSearchSubmit: (params: HotelListingSearchSubmitParams) => void
}

function useHotelListingSearchControls({ searchValue, searchItem, hotelDateSelection, onHotelDateSelectionChange, rooms: defaultRooms, maxGuests = HOTEL_DRAWER_MAX_GUESTS, onRoomsChange, onSearchSubmit }: HotelListingSearchControlsParams) {
  const intl = useIntl()
  const [isMobileHotelEditorOpen, , openMobileHotelEditor, closeMobileHotelEditor] = useToggle()
  const hotelDateGuestsRef = useRef<ListingDrawerHotelDateGuestsHandle>(null)
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const isNarrowViewport = !useScreenSizeUp('tablet')
  useEffect(() => {
    if (!isNarrowViewport) closeMobileHotelEditor()
  }, [closeMobileHotelEditor, isNarrowViewport])
  const isCompactDatePicker = isNarrowViewport
  const pendingDateTargetRef = useRef<App.SearchItem | undefined>(undefined)
  const [draftSearch, setDraftSearch] = useState(searchValue)
  const [draftSearchItem, setDraftSearchItem] = useState<App.SearchItem | undefined>(undefined)
  // Seeded party from flights/chat may include children without ages (-1 placeholders).
  // Treat that like a failed Apply: show Guests error + open the picker once.
  // Mid-edit invalid occupancy (e.g. adding a child) does not show the banner until Apply/Search.
  const initiallyIncompleteOccupancy =
    defaultRooms.length > 0 &&
    !isValidOccupancy(...defaultRooms)
  const committedHotelDateSelection = hotelDateSelection
  const { globalSearchState: searchState, globalSearchDispatch: searchDispatch } = useGlobalSearchContext({
    ...createHotelSearchState({ searchValue, searchItem, hotelDateSelection, occupancies: defaultRooms }),
    areRoomErrorsShown: initiallyIncompleteOccupancy,
    activeMenu: initiallyIncompleteOccupancy && !isCompactDatePicker ? SearchMenuStates.Rooms : SearchMenuStates.Closed,
  })
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    pendingDateTargetRef.current = undefined
    setDraftSearch(searchValue)
    setDraftSearchItem(searchItem)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue),
    })
  }, [searchDispatch, searchItem, searchValue])

  const rooms = useMemo(
    () => searchState.occupancies ?? defaultRooms,
    [defaultRooms, searchState.occupancies],
  )
  const areRoomsValid = rooms.length > 0 && sumUpOccupancies(rooms) <= maxGuests && isValidOccupancy(...rooms)

  const clearRoomErrors = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: false })
  }, [searchDispatch])

  const discardPendingDateTarget = useCallback(() => {
    if (!pendingDateTargetRef.current) return
    pendingDateTargetRef.current = undefined
    setDraftSearch(searchValue)
    setDraftSearchItem(searchItem)
    searchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue) })
  }, [searchDispatch, searchItem, searchValue])

  const commitHotelDateSelection = useCallback((
    hotelDatesOverride?: ListingDrawerHotelDateSelection,
  ) => {
    const resolvedOverride = isListingDrawerHotelDateSelection(hotelDatesOverride)
      ? hotelDatesOverride
      : undefined
    const nextSelection = resolvedOverride ?? getHotelDateSelectionFromSearchState(searchState)

    onHotelDateSelectionChange(nextSelection, pendingDateTargetRef.current)
    pendingDateTargetRef.current = undefined
  }, [onHotelDateSelectionChange, searchState])

  const acceptHotelRooms = useCallback((nextRooms: Array<App.Occupants>) => {
    onRoomsChange(nextRooms)
  }, [onRoomsChange])

  // Clear Guests error once occupancy is valid again. Do not auto-show on mid-edit
  // invalidation — only initial seed, Apply, and Search set areRoomErrorsShown.
  useEffect(() => {
    if (!areRoomsValid || !searchState.areRoomErrorsShown) return
    clearRoomErrors()
  }, [areRoomsValid, clearRoomErrors, searchState.areRoomErrorsShown])

  const searchButtonLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })

  const { primaryText: summaryDates, secondaryText: summaryGuests } = useMemo(() => getHotelSearchSummary(intl, {
    ...getHotelSearchDateState(hotelDateSelection),
    occupancies: defaultRooms,
  }), [defaultRooms, hotelDateSelection, intl])

  const recentSearchItems = useMemo(() => getRecentSearches(), [])

  const handleSearchItemChange = useCallback((nextSearchItem?: App.SearchItem) => {
    setDraftSearchItem(nextSearchItem)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: nextSearchItem ?? buildDisplayOnlySearchItem(draftSearch),
    })
    if (nextSearchItem) {
      setDraftSearch(nextSearchItem.format.mainText)
    }
  }, [draftSearch, searchDispatch])

  const openHotelDatePicker = useCallback((propertySearchItem?: App.SearchItem) => {
    pendingDateTargetRef.current = propertySearchItem
    if (propertySearchItem) {
      handleSearchItemChange(propertySearchItem)
    }

    controlsContainerRef.current?.scrollIntoView({ block: 'nearest' })

    hotelDateGuestsRef.current?.openDates()
  }, [handleSearchItemChange])

  const applySearch = useCallback((hotelDatesOverride?: ListingDrawerHotelDateSelection) => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    const trimmedSearch = draftSearch.trim()
    const submitSearchItem = getSubmitSearchItem(trimmedSearch, draftSearchItem)
    const nextSelection = isListingDrawerHotelDateSelection(hotelDatesOverride)
      ? hotelDatesOverride
      : getHotelDateSelectionFromSearchState(searchState)

    onSearchSubmit({
      searchValue: trimmedSearch,
      searchItem: submitSearchItem,
      hotelDateSelection: nextSelection,
      occupancies: rooms,
    })
  }, [
    areRoomsValid,
    draftSearch,
    draftSearchItem,
    onSearchSubmit,
    rooms,
    searchState,
    searchDispatch,
  ])

  const handlePhraseChange = useCallback((value: string) => {
    setDraftSearch(value)
    setDraftSearchItem((searchItem) => {
      if (!searchItem || searchItem.format.mainText.trim() === value.trim()) return searchItem
      return undefined
    })
  }, [])

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applySearch()
  }, [applySearch])

  return {
    searchState,
    searchDispatch,
    controlsContainerRef,
    searchInputRef,
    hotelDateGuestsRef,
    isNarrowViewport,
    isMobileHotelEditorOpen,
    openMobileHotelEditor,
    closeMobileHotelEditor,
    committedHotelDateSelection,
    defaultRooms,
    summaryDates,
    summaryGuests,
    searchButtonLabel,
    recentSearchItems,
    handleSearchItemChange,
    handlePhraseChange,
    handleSubmit,
    commitHotelDateSelection,
    acceptHotelRooms,
    discardPendingDateTarget,
    applySearch,
    submitMobileHotelSearch: onSearchSubmit,
    openHotelDatePicker,
  }
}

export default useHotelListingSearchControls
